/**
 * What a live break's page says that no store writes: why a purchase waits or was refused, the
 * host commission in its prices, the closed page's countdown, a removed shopper's and a failed
 * read's screens, the failures beside the queue and the results, the page's toasts, a Twitch
 * link's failures, the closed live break's read-only chat, and a create refused because the
 * store's own live break is already open. Platform words (the rule is in `storefrontCopy/shared.ts`): the same on every
 * store apart from its terms and currency, so never a store's copy. The storefront's live break
 * page and the design system's stories read them from here; what a purchase holds and that an
 * Opening may not reach the stream are `liveBreakDisclosure.ts`'s.
 *
 * A purchase here is a queue entry and its turn on stage an Opening; "break" is never said alone
 * (CONTEXT.md). Every amount arrives formatted in the store's currency.
 */
import type {
  LiveBreakBoxIneligibleReason,
  LiveBreakEntryNotCancellableReason,
  LiveBreakNotAcceptingReason,
  LiveBreakQueueFullReason,
  LiveBreakQueueTier,
  TwitchLinkError,
} from "./liveBreaks.js";
import { type StorefrontTerminology, termCount } from "./storefrontTerminology.js";

/** A line with a short title and, optionally, the sentence under it. */
export interface LiveBreakPlatformNotice {
  title: string;
  body?: string;
}

/** Why buying waits, short of a full queue: closed, Draining, the host away, Purchases stopped. */
export type LiveBreakBuyBlockKind = "closed" | "draining" | "hostAway" | "stopped";

/**
 * Why a purchase was refused, as the page tells it: by the refusal's code and, where the code has
 * several causes, its `details.reason` (and what the reason names).
 */
export type LiveBreakPurchaseRefusal =
  | { code: "queue_full"; reason: LiveBreakQueueFullReason | null }
  | { code: "price_changed" }
  | {
      code: "box_not_eligible";
      reason: LiveBreakBoxIneligibleReason | null;
      /** The live break's minimum box price, formatted, for `below_min_price`. */
      minPrice?: string | null;
    }
  | { code: "not_accepting"; reason: LiveBreakNotAcceptingReason | null }
  | { code: "closed" }
  | { code: "ended" }
  | { code: "insufficient_balance" }
  | { code: "account_locked" }
  | { code: "paused" }
  | { code: "disabled" }
  | { code: "unknown" };

/** The live break page's platform words, in the store's terms. */
export interface LiveBreakPlatformWords {
  /** Why buying waits, said under the purchase. */
  blocks: Record<LiveBreakBuyBlockKind, LiveBreakPlatformNotice>;
  /** A full queue: the shopper waits out a short block, counted down. */
  retry: { title: string; body: (secondsLeft: number) => string };
  /** In the block's place while nothing blocks: the prices carry the host's commission. */
  notice: string;
  /** Over the stage once it has closed, counting down to the directory. */
  closed: { title: string; countdown: string; leaving: string };
  /** In place of the stage for a shopper the host removed. */
  removed: LiveBreakPlatformNotice;
  /** In place of the stage when the live break could not be read, or is gone. */
  failed: LiveBreakPlatformNotice;
  missing: LiveBreakPlatformNotice;
  /** Beside the queue, and in the results, when their read failed. */
  queueFailed: string;
  resultsFailed: string;
  /** Under the results when a sale or a claim failed. */
  saleFailed: string;
  claimFailed: string;
  /** Under the results' Claim while it waits on another reveal of the shopper's to be claimed. */
  claimWaits: string;
  /** Why a purchase was refused (a toast). */
  purchaseRefused: (refusal: LiveBreakPurchaseRefusal, unit: string) => string;
  /** Why leaving the queue was refused (a toast); null reason for any other failure. */
  leaveRefused: (reason: LiveBreakEntryNotCancellableReason | null) => string;
  /** The join dialog's reason while a purchase it shows can no longer be made. */
  unavailable: string;
  /** In the chat composer's place once the live break has closed: the room is read-only. */
  chatClosed: string;
  toasts: {
    /** A purchase joined the queue: how many, and the tier it got. */
    queued: (amount: number, tier: LiveBreakQueueTier) => string;
    /** The shopper left the queue; `amount` is the hold released, formatted. */
    left: (amount: string) => string;
    /**
     * A viewer who saw it open: the host ended it (named, or null on a house live break), and its
     * queue finishes opening.
     */
    ended: (hostName: string | null) => string;
    /** Entries of the shopper's were released (the close, staff); the holds are back. */
    released: (count: number) => string;
    /** A purchase waited on the shopper's own reveal here, still opening on the stage. */
    revealStillOpening: string;
    /** The live break's link was copied. */
    linkCopied: string;
  };
  /** Why a Twitch link failed (a toast), or null where the shopper chose not to (`access_denied`). */
  twitchLinkFailed: (error: TwitchLinkError) => string | null;
  /** Disconnect Twitch failed (a toast, on the account's Twitch connection). */
  twitchUnlinkFailed: string;
  /**
   * A create refused with `live_break_already_open`: the store's hosts go live one live break at a
   * time and one is open, said in Create's wizard with the way to it.
   */
  storeLiveBreakOpen: { message: string; action: string };
  /** The toolbar's fairness dialog: how a live break's rolls verify, and its way to each one. */
  fairness: {
    body: string;
    /** Over the latest Opening's pulls, and said while none has opened yet. */
    latest: string;
    none: string;
    /** A pull's link to the verifier, read with the item's name. */
    verify: string;
    verifyLabel: (itemName: string) => string;
    /** The way to the fairness page. */
    page: string;
    close: string;
  };
}

export function liveBreakPlatformWords(terms: StorefrontTerminology): LiveBreakPlatformWords {
  const { box, item } = terms;
  const blocks: Record<LiveBreakBuyBlockKind, LiveBreakPlatformNotice> = {
    closed: { title: "This live break has closed" },
    // "Ending" while the queue still opens, "closed" once it is over, as the status badge says.
    draining: {
      title: "This live break is ending",
      body: "No new entries; the queue is still opening",
    },
    hostAway: { title: "The host stepped away", body: "Buying waits until they're back" },
    stopped: { title: "The host stopped purchases", body: `No new ${box.plural} for now` },
  };
  const notAccepting = (reason: LiveBreakNotAcceptingReason | null): string => {
    const block =
      reason === "draining"
        ? blocks.draining
        : reason === "host_away"
          ? blocks.hostAway
          : blocks.stopped;
    return block.body ? `${block.title}. ${block.body}.` : `${block.title}.`;
  };
  return {
    blocks,
    retry: {
      title: "The queue is full",
      body: (seconds) => `Try again in ${seconds} ${seconds === 1 ? "second" : "seconds"}`,
    },
    notice: "Prices here include the host's commission.",
    closed: {
      title: "This live break has closed",
      countdown: "Back to live breaks in",
      leaving: "Taking you back to live breaks…",
    },
    removed: {
      title: "You've been removed from this live break",
      body: "Anything you had queued here was released, and its hold returned to your balance.",
    },
    failed: {
      title: "Couldn't load this live break",
      body: "Something went wrong reaching the store.",
    },
    missing: {
      title: "This live break isn't here",
      body: "It may have closed a while ago, or the link is wrong.",
    },
    queueFailed: "Couldn't load the queue.",
    resultsFailed: `Couldn't load your ${item.plural}.`,
    saleFailed: "The sale didn't go through. Nothing was sold.",
    claimFailed: "The claim didn't go through. Nothing was charged.",
    claimWaits: "Claim your other reveal first.",
    purchaseRefused: (refusal, unit) => {
      switch (refusal.code) {
        case "queue_full":
          return refusal.reason === "buyer"
            ? "You have as many entries queued here as this live break allows."
            : "The queue is full. Try again in a moment.";
        case "price_changed":
          return "The price changed. Check the new price and try again.";
        case "box_not_eligible":
          switch (refusal.reason) {
            case "below_min_price":
              return refusal.minPrice
                ? `That ${box.singular} is below this live break's minimum price of ${refusal.minPrice}.`
                : `That ${box.singular} is below this live break's minimum price.`;
            case "not_in_live_break":
              return `This live break only sells its own ${box.plural}.`;
            case "not_openable":
              return `That ${box.singular} can't be opened right now.`;
            default:
              return `That ${box.singular} isn't sold in live breaks.`;
          }
        case "not_accepting":
          return notAccepting(refusal.reason);
        case "closed":
          return "This live break has closed.";
        case "ended":
          return "This live break has ended.";
        case "insufficient_balance":
          return `Not enough ${unit} to join the queue.`;
        case "account_locked":
          return "Your account is locked, so you can't join the queue.";
        case "paused":
          return "Opening is paused right now. Try again later.";
        case "disabled":
          return "Live breaks are switched off for this store.";
        default:
          return "Couldn't join the queue. Try again.";
      }
    },
    leaveRefused: (reason) => {
      switch (reason) {
        case "up_next":
          return "That entry is up next, so it can't be cancelled.";
        case "presenting":
          return "That entry is on stage, so it can't be cancelled.";
        case "not_queued":
          return "That entry has already left the queue.";
        default:
          return "Couldn't leave the queue. Try again.";
      }
    },
    unavailable: "This purchase is no longer available.",
    chatClosed: "This live break has closed. Chat is read-only.",
    toasts: {
      queued: (amount, tier) => {
        const added = `Added ${termCount(amount, box)}`;
        if (tier === "next") {
          return `${added} to the front of the queue. ${amount === 1 ? "It opens" : "They open"} after the entry up next.`;
        }
        return tier === "priority"
          ? `${added} to the queue with Priority.`
          : `${added} to the queue.`;
      },
      left: (amount) => `You left the queue. ${amount} is back in your balance.`,
      ended: (hostName) =>
        `${hostName === null ? "This live break" : `${hostName}'s live break`} has ended. The queue finishes opening, and no new entries are taken.`,
      released: (count) =>
        count === 1
          ? "Your queued entry was released, and its hold is back in your balance."
          : `${count} of your queued entries were released, and their holds are back in your balance.`,
      revealStillOpening: `Your ${item.plural} can be claimed once your opening finishes.`,
      linkCopied: "Link copied.",
    },
    fairness: {
      body: "Every opening in a live break is an ordinary open, rolled on its buyer's own seed and nonce. Server seeds are committed by hash before the day starts and revealed after it ends, so any pull can be recomputed in your browser.",
      latest: "The latest opening",
      none: "Nothing has opened here yet.",
      verify: "Verify",
      verifyLabel: (itemName) => `Verify ${itemName}`,
      page: "Open the fairness page",
      close: "Close",
    },
    twitchLinkFailed: (error) => {
      switch (error) {
        case "access_denied":
          return null;
        case "twitch_already_linked":
          return "That Twitch account is already linked to another account on this store.";
        case "twitch_code_rejected":
          return "Twitch rejected the sign-in. Try again.";
        case "twitch_unavailable":
          return "Twitch is unavailable right now. Try again.";
        case "not_signed_in":
          return "Sign in to connect Twitch.";
        case "twitch_not_enabled":
          return "Twitch isn't available on this store.";
        default:
          return "Couldn't connect your Twitch account. Try again.";
      }
    },
    twitchUnlinkFailed: "Couldn't disconnect your Twitch account. Try again.",
    storeLiveBreakOpen: {
      message: "This store already has a live break open. Only one runs at a time.",
      action: "Go to the live break",
    },
  };
}
